import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  Alert,
  Snackbar,
  IconButton,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  ListItemButton,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Person as PersonIcon,
  Security as SecurityIcon,
  Business as BusinessIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  Close as CloseIcon,
  People as PeopleIcon,
  Mail as MailIcon,
  AccountBalanceWallet as WalletIcon,
  Subscriptions as SubscriptionsIcon,
} from '@mui/icons-material';
import { organizationApi, Organization, OrganizationMember, OrganizationRole, InvitationStatus, OrganizationInvitation, getInvitationStatusLabel, canViewMembers, canViewInvitations } from '../services/organizationApi';
import { userApi, UserProfile, ProfileUpdateRequest, PasswordChangeRequest, PreferencesUpdateRequest } from '../services/userApi';
import SubscriptionManager from './SubscriptionManager';

interface ProfileModalProps {
  open: boolean;
  onClose: () => void;
}

// Custom TabPanel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`org-tabpanel-${index}`}
      aria-labelledby={`org-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ProfileModal: React.FC<ProfileModalProps> = ({ open, onClose }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Profile editing state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editForm, setEditForm] = useState<ProfileUpdateRequest | null>(null);

  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // Preferences state
  const [preferences, setPreferences] = useState<any>(null);

  // Create organization state
  const [showCreateOrgDialog, setShowCreateOrgDialog] = useState(false);
  const [createOrgForm, setCreateOrgForm] = useState({
    name: '',
    description: '',
    website: '',
    industry: '',
    size: '',
  });

  // Organization details and member management state
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [showOrgDetailsDialog, setShowOrgDetailsDialog] = useState(false);
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [addMemberForm, setAddMemberForm] = useState({
    email: '',
    role: OrganizationRole.MEMBER,
    message: '',
  });

  // New state for organization details
  const [orgDetailsTab, setOrgDetailsTab] = useState(0);
  const [orgMembers, setOrgMembers] = useState<OrganizationMember[]>([]);
  const [orgInvitations, setOrgInvitations] = useState<OrganizationInvitation[]>([]);
  const [loadingOrgDetails, setLoadingOrgDetails] = useState(false);

  const loadProfile = useCallback(async () => {
    try {
      setLoading(true);
      const userProfile = await userApi.getProfile();
      setProfile(userProfile);
      setEditForm({
        username: userProfile.username,
        firstName: userProfile.firstName,
        lastName: userProfile.lastName,
        location: userProfile.location,
        company: userProfile.company,
        bio: userProfile.bio,
        avatar: userProfile.avatar,
      });
      setPreferences(userProfile.preferences);
    } catch (error) {
      console.error('Error loading profile:', error);
      showSnackbar('Failed to load profile', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUserData = useCallback(async () => {
    try {
      const orgs = await organizationApi.getMyOrganizations();
      setOrganizations(orgs);
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  }, []);

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  useEffect(() => {
    if (open) {
      loadProfile();
      loadUserData();
    }
  }, [open, loadProfile, loadUserData]);

  const handleTabChange = (newValue: number) => {
    setActiveTab(newValue);
  };

  const handlePasswordChange = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showSnackbar('New passwords do not match', 'error');
      return;
    }

    try {
      setLoading(true);
      const request: PasswordChangeRequest = {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      };
      await userApi.changePassword(request);
      showSnackbar('Password changed successfully', 'success');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Error changing password:', error);
      showSnackbar('Failed to change password', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSave = async () => {
    if (!editForm) return;

    try {
      setLoading(true);
      const updatedProfile = await userApi.updateProfile(editForm);
      setProfile(updatedProfile);
      setIsEditingProfile(false);
      showSnackbar('Profile updated successfully', 'success');
    } catch (error) {
      console.error('Error updating profile:', error);
      showSnackbar('Failed to update profile', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileCancel = () => {
    setIsEditingProfile(false);
    if (profile) {
              setEditForm({
          username: profile.username,
          firstName: profile.firstName,
          lastName: profile.lastName,
          location: profile.location,
          company: profile.company,
          bio: profile.bio,
          avatar: profile.avatar,
        });
    }
  };

  const handlePreferenceChange = (category: string, key: string, value: any) => {
    if (!preferences) return;
    
    setPreferences({
      ...preferences,
      [category]: {
        ...preferences[category],
        [key]: value,
      },
    });
  };

  const handlePreferencesSave = async () => {
    if (!preferences) return;

    try {
      setLoading(true);
      const request: PreferencesUpdateRequest = preferences;
      await userApi.updatePreferences(request);
      showSnackbar('Preferences updated successfully', 'success');
    } catch (error) {
      console.error('Error updating preferences:', error);
      showSnackbar('Failed to update preferences', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getRoleLabel = (role: OrganizationRole | string | number) => {
    try {
      const roleValue = typeof role === 'string' ? parseInt(role) : role;
      switch (roleValue) {
        case OrganizationRole.OWNER:
        case 0: return 'Owner';
        case OrganizationRole.ADMIN:
        case 10: return 'Admin';
        case OrganizationRole.MEMBER:
        case 20: return 'Member';
        case OrganizationRole.VIEWER:
        case 30: return 'Viewer';
        default: return `Role ${roleValue}`;
      }
    } catch (error) {
      console.error('Error getting role label for role:', role, error);
      return 'Unknown Role';
    }
  };

  const getRoleColor = (role: OrganizationRole | string | number) => {
    try {
      const roleValue = typeof role === 'string' ? parseInt(role) : role;
      switch (roleValue) {
        case OrganizationRole.OWNER:
        case 0: return 'error';
        case OrganizationRole.ADMIN:
        case 10: return 'warning';
        case OrganizationRole.MEMBER:
        case 20: return 'default';
        case OrganizationRole.VIEWER:
        case 30: return 'info';
        default: return 'default';
      }
    } catch (error) {
      console.error('Error getting role color for role:', role, error);
      return 'default';
    }
  };

  const handleCreateOrganization = () => {
    setShowCreateOrgDialog(true);
  };

  const handleCreateOrgSubmit = async () => {
    try {
      setLoading(true);
      const newOrg = await organizationApi.createOrganization(createOrgForm);
      
      // Add the new organization to the list
      setOrganizations([...organizations, newOrg]);
      
      // Reset form and close dialog
      setCreateOrgForm({
        name: '',
        description: '',
        website: '',
        industry: '',
        size: '',
      });
      setShowCreateOrgDialog(false);
      
      showSnackbar('Organization created successfully!', 'success');
      
      // Reload user data to get updated memberships
      loadUserData();
    } catch (error) {
      console.error('Error creating organization:', error);
      showSnackbar('Failed to create organization', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrgCancel = () => {
    setCreateOrgForm({
      name: '',
      description: '',
      website: '',
      industry: '',
      size: '',
    });
    setShowCreateOrgDialog(false);
  };

  const handleOrgClick = (org: Organization) => {
    setSelectedOrg(org);
    setOrgDetailsTab(0); // Reset to Overview tab
    setShowOrgDetailsDialog(true);
    loadOrganizationDetails(org.id);
  };

  const handleOrgDetailsDialogClose = () => {
    setShowOrgDetailsDialog(false);
    setSelectedOrg(null);
    setOrgDetailsTab(0);
    setOrgMembers([]);
    setOrgInvitations([]);
  };

  const loadOrganizationDetails = async (orgId: string) => {
    try {
      setLoadingOrgDetails(true);
      
      // Always fetch members (regular members can see other members)
      const membersData = await organizationApi.getOrganizationMembers(orgId);
      
      // Process the members data - handle different possible response structures
      let actualMembersData = membersData;
      if (membersData && typeof membersData === 'object' && !Array.isArray(membersData)) {
        const membersObj = membersData as any;
        if (membersObj.data && Array.isArray(membersObj.data)) {
          actualMembersData = membersObj.data;
        } else if (membersObj.members && Array.isArray(membersObj.members)) {
          actualMembersData = membersObj.members;
        } else if (membersObj.results && Array.isArray(membersObj.results)) {
          actualMembersData = membersObj.results;
        }
      }
      
      setOrgMembers(actualMembersData || []);
      
      // Only fetch invitations if user has permission to view them
      if (selectedOrg && selectedOrg.user_role && canViewInvitations(selectedOrg.user_role)) {
        try {
          const invitationsData = await organizationApi.getOrganizationInvitations(orgId);
          setOrgInvitations(invitationsData || []);
        } catch (invitationError) {
          console.warn('User does not have permission to view invitations:', invitationError);
          setOrgInvitations([]);
        }
      } else {
        setOrgInvitations([]);
      }
    } catch (error) {
      console.error('Error loading organization details:', error);
      showSnackbar('Failed to load organization details', 'error');
      // Set empty arrays on error to prevent undefined issues
      setOrgMembers([]);
      setOrgInvitations([]);
    } finally {
      setLoadingOrgDetails(false);
    }
  };

  const handleAddMember = (org: Organization) => {
    setSelectedOrg(org);
    setShowAddMemberDialog(true);
  };

  const handleAddMemberSubmit = async () => {
    if (!selectedOrg) return;
    
    try {
      setLoading(true);
      await organizationApi.createInvitation(
        selectedOrg.id,
        {
          email: addMemberForm.email,
          role: addMemberForm.role,
          message: addMemberForm.message,
        }
      );
      
      showSnackbar('Invitation sent successfully!', 'success');
      setAddMemberForm({
        email: '',
        role: OrganizationRole.MEMBER,
        message: '',
      });
      setShowAddMemberDialog(false);
      setSelectedOrg(null);
      
      // Refresh the organization details if the dialog is still open
      if (showOrgDetailsDialog && selectedOrg) {
        loadOrganizationDetails(selectedOrg.id);
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      showSnackbar('Failed to send invitation', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMemberCancel = () => {
    setAddMemberForm({
      email: '',
      role: OrganizationRole.MEMBER,
      message: '',
    });
    setShowAddMemberDialog(false);
    setSelectedOrg(null);
  };

  const tabItems = [
    { label: 'General', icon: <PersonIcon />, index: 0 },
    { label: 'Security', icon: <SecurityIcon />, index: 1 },
    { label: 'Organizations', icon: <BusinessIcon />, index: 2 },
    { label: 'Preferences', icon: <SettingsIcon />, index: 3 },
    { label: 'Tokens', icon: <WalletIcon />, index: 4 },
    { label: 'Subscriptions', icon: <SubscriptionsIcon />, index: 5 },
  ];

  if (!profile) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Profile & Settings</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6">Loading profile...</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5">Profile & Settings</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', height: '70vh' }}>
          {/* Left Sidebar Navigation */}
          <Box sx={{ width: 240, borderRight: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
            {/* Profile Header */}
            <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  src={profile.avatar}
                  sx={{ width: 48, height: 48, fontSize: '1.2rem' }}
                >
                  {profile.firstName?.[0] || profile.lastName?.[0] || profile.username[0]}
                </Avatar>
                <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    noWrap
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {profile.firstName && profile.lastName ? `${profile.firstName} ${profile.lastName}` : profile.username}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    @{profile.username}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Navigation Tabs */}
            <List sx={{ pt: 1 }}>
              {tabItems.map((item) => (
                <ListItem key={item.index} disablePadding>
                  <ListItemButton
                    selected={activeTab === item.index}
                    onClick={() => handleTabChange(item.index)}
                    sx={{
                      mx: 1,
                      borderRadius: 1,
                      '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        '&:hover': {
                          bgcolor: 'primary.dark',
                        },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ 
                      minWidth: 40,
                      color: activeTab === item.index ? 'inherit' : 'text.secondary'
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Right Content Area */}
          <Box sx={{ flexGrow: 1, p: 3, overflow: 'auto' }}>
            {/* General Tab */}
            {activeTab === 0 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6">Personal Information</Typography>
                  {!isEditingProfile && (
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => setIsEditingProfile(true)}
                    >
                      Edit Profile
                    </Button>
                  )}
                </Box>

                {isEditingProfile && editForm ? (
                  <Box sx={{ maxWidth: 600 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
                      <TextField
                        fullWidth
                        label="First Name"
                        value={editForm.firstName || ''}
                        onChange={(e) => editForm && setEditForm({ ...editForm, firstName: e.target.value })}
                      />
                      <TextField
                        fullWidth
                        label="Last Name"
                        value={editForm.lastName || ''}
                        onChange={(e) => editForm && setEditForm({ ...editForm, lastName: e.target.value })}
                      />
                      <TextField
                        fullWidth
                        label="Username"
                        value={editForm.username || ''}
                        onChange={(e) => editForm && setEditForm({ ...editForm, username: e.target.value })}
                      />

                      <TextField
                        fullWidth
                        label="Location"
                        value={editForm.location || ''}
                        onChange={(e) => editForm && setEditForm({ ...editForm, location: e.target.value })}
                      />
                      <TextField
                        fullWidth
                        label="Company"
                        value={editForm.company || ''}
                        onChange={(e) => editForm && setEditForm({ ...editForm, company: e.target.value })}
                      />
                      <TextField
                        fullWidth
                        label="Bio"
                        multiline
                        rows={4}
                        value={editForm.bio || ''}
                        onChange={(e) => editForm && setEditForm({ ...editForm, bio: e.target.value })}
                        sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}
                      />
                    </Box>
                    <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleProfileSave}
                        disabled={loading}
                      >
                        Save Changes
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={handleProfileCancel}
                        disabled={loading}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ maxWidth: 600 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <PersonIcon color="action" />
                        <Box>
                          <Typography variant="body2" color="text.secondary">First Name</Typography>
                          <Typography variant="body1">{profile.firstName || 'Not provided'}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <PersonIcon color="action" />
                        <Box>
                          <Typography variant="body2" color="text.secondary">Last Name</Typography>
                          <Typography variant="body1">{profile.lastName || 'Not provided'}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <PersonIcon color="action" />
                        <Box>
                          <Typography variant="body2" color="text.secondary">Username</Typography>
                          <Typography variant="body1">@{profile.username}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <EmailIcon color="action" />
                        <Box>
                          <Typography variant="body2" color="text.secondary">Email</Typography>
                          <Typography variant="body1">{profile.email}</Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <LocationIcon color="action" />
                        <Box>
                          <Typography variant="body2" color="text.secondary">Location</Typography>
                          <Typography variant="body1">{profile.location || 'Not provided'}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <WorkIcon color="action" />
                        <Box>
                          <Typography variant="body2" color="text.secondary">Company</Typography>
                          <Typography variant="body1">{profile.company || 'Not provided'}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2, gridColumn: { xs: '1', sm: '1 / -1' } }}>
                        <PersonIcon color="action" sx={{ mt: 0.5 }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">Bio</Typography>
                          <Typography variant="body1">{profile.bio || 'No bio provided'}</Typography>
                        </Box>
                      </Box>

                      {/* Token Information */}
                      <Box sx={{ 
                        gridColumn: { xs: '1', sm: '1 / -1' },
                        mb: 3
                      }}>
                        <Typography variant="h6" gutterBottom sx={{ 
                          fontWeight: 600, 
                          color: 'text.primary',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}>
                          <WalletIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                          Token Balance
                        </Typography>
                        <Card sx={{ 
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          borderRadius: 3,
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                          border: 'none',
                          position: 'relative',
                          overflow: 'hidden'
                        }}>
                          {/* Decorative elements */}
                          <Box sx={{
                            position: 'absolute',
                            top: -20,
                            right: -20,
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            background: 'rgba(255, 255, 255, 0.1)',
                            backdropFilter: 'blur(10px)'
                          }} />
                          <Box sx={{
                            position: 'absolute',
                            bottom: -30,
                            left: -30,
                            width: 100,
                            height: 100,
                            borderRadius: '50%',
                            background: 'rgba(255, 255, 255, 0.08)',
                            backdropFilter: 'blur(10px)'
                          }} />
                          
                          <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                            <Box sx={{ 
                              display: 'grid', 
                              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                              gap: 3,
                              mb: 2
                            }}>
                              {/* Available Tokens */}
                              <Box sx={{ 
                                textAlign: 'center',
                                p: 2,
                                borderRadius: 2,
                                background: 'rgba(255, 255, 255, 0.15)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255, 255, 255, 0.2)'
                              }}>
                                <Typography variant="h4" sx={{ 
                                  fontWeight: 700, 
                                  color: '#4ade80',
                                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                  mb: 0.5
                                }}>
                                  {profile.tokens?.available || 0}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  color: 'rgba(255, 255, 255, 0.9)',
                                  fontWeight: 500,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  fontSize: '0.75rem'
                                }}>
                                  Available
                                </Typography>
                              </Box>

                              {/* Used Tokens */}
                              <Box sx={{ 
                                textAlign: 'center',
                                p: 2,
                                borderRadius: 2,
                                background: 'rgba(255, 255, 255, 0.15)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255, 255, 255, 0.2)'
                              }}>
                                <Typography variant="h4" sx={{ 
                                  fontWeight: 700, 
                                  color: '#fbbf24',
                                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                  mb: 0.5
                                }}>
                                  {profile.tokens?.used || 0}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  color: 'rgba(255, 255, 255, 0.9)',
                                  fontWeight: 500,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  fontSize: '0.75rem'
                                }}>
                                  Used
                                </Typography>
                              </Box>

                              {/* Purchased Tokens */}
                              <Box sx={{ 
                                textAlign: 'center',
                                p: 2,
                                borderRadius: 2,
                                background: 'rgba(255, 255, 255, 0.15)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255, 255, 255, 0.2)'
                              }}>
                                <Typography variant="h4" sx={{ 
                                  fontWeight: 700, 
                                  color: '#60a5fa',
                                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                  mb: 0.5
                                }}>
                                  {profile.tokens?.purchased || 0}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  color: 'rgba(255, 255, 255, 0.9)',
                                  fontWeight: 500,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  fontSize: '0.75rem'
                                }}>
                                  Purchased
                                </Typography>
                              </Box>
                            </Box>

                            {/* Last Purchase Info */}
                            {profile.tokens?.lastPurchase && (
                              <Box sx={{ 
                                mt: 2, 
                                pt: 2, 
                                borderTop: '1px solid rgba(255, 255, 255, 0.2)',
                                textAlign: 'center'
                              }}>
                                <Typography variant="caption" sx={{ 
                                  color: 'rgba(255, 255, 255, 0.8)',
                                  fontWeight: 500
                                }}>
                                  Last purchase: {new Date(profile.tokens.lastPurchase).toLocaleDateString()}
                                </Typography>
                              </Box>
                            )}

                            {/* Quick Action Button */}
                            <Box sx={{ mt: 2, textAlign: 'center' }}>
                              <Button
                                variant="contained"
                                size="small"
                                sx={{
                                  background: 'rgba(255, 255, 255, 0.2)',
                                  color: 'white',
                                  border: '1px solid rgba(255, 255, 255, 0.3)',
                                  borderRadius: 2,
                                  px: 3,
                                  py: 1,
                                  fontWeight: 600,
                                  textTransform: 'none',
                                  '&:hover': {
                                    background: 'rgba(255, 255, 255, 0.3)',
                                    border: '1px solid rgba(255, 255, 255, 0.4)',
                                  }
                                }}
                                onClick={() => setActiveTab(4)} // Switch to Tokens tab
                              >
                                Manage Tokens
                              </Button>
                            </Box>
                          </CardContent>
                        </Card>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {/* Security Tab */}
            {activeTab === 1 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Change Password
                </Typography>
                <Alert severity="info" sx={{ mb: 3 }}>
                  Your password must be at least 8 characters long and should include a mix of letters, numbers, and special characters.
                </Alert>
                
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 3, maxWidth: 600 }}>
                  <TextField
                    fullWidth
                    label="Current Password"
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                        >
                          {showPasswords.current ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    label="New Password"
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                        >
                          {showPasswords.new ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                        >
                          {showPasswords.confirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      ),
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handlePasswordChange}
                    disabled={loading || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
                  >
                    Change Password
                  </Button>
                </Box>

                <Divider sx={{ my: 4 }} />

                <Typography variant="h6" gutterBottom>
                  Two-Factor Authentication
                </Typography>
                <Alert severity="info" sx={{ mb: 3 }}>
                  Enhance your account security by enabling two-factor authentication. This adds an extra layer of protection to your account.
                </Alert>
                <Button variant="outlined">
                  Enable 2FA
                </Button>
              </Box>
            )}

            {/* Organizations Tab */}
            {activeTab === 2 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6">
                    Your Organizations
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<BusinessIcon />}
                    onClick={handleCreateOrganization}
                  >
                    Create Organization
                  </Button>
                </Box>
                
                {organizations.length === 0 ? (
                  <Alert severity="info">
                    You are not a member of any organizations yet.
                  </Alert>
                ) : (
                  <Box sx={{ display: 'grid', gap: 2, maxWidth: 800 }}>
                    {organizations.map((org) => {
                      // Use the user_role field from the organization response
                      const userRole = typeof org.user_role === 'string' ? parseInt(org.user_role) : null;
                      
                      // Only show as owner if actually has OWNER role, and only allow adding members if owner or admin
                      const canAddMembers = userRole !== null && (userRole === OrganizationRole.OWNER || userRole === OrganizationRole.ADMIN);
                      
                      return (
                        <Card 
                          key={org.id} 
                          variant="outlined" 
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { 
                              boxShadow: 2,
                              borderColor: 'primary.main' 
                            }
                          }}
                          onClick={() => handleOrgClick(org)}
                        >
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box>
                                <Typography variant="h6">{org.name}</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  {org.description}
                                </Typography>
                                {userRole !== null ? (
                                  <Chip 
                                    label={getRoleLabel(userRole)} 
                                    color={getRoleColor(userRole)}
                                    size="small"
                                  />
                                ) : (
                                  <Chip 
                                    label="Unknown Role" 
                                    color="default"
                                    size="small"
                                  />
                                )}
                              </Box>
                              {canAddMembers && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<PersonIcon />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddMember(org);
                                  }}
                                >
                                  Add Member
                                </Button>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}

            {/* Preferences Tab */}
            {activeTab === 3 && preferences && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Preferences
                </Typography>
                
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4, maxWidth: 800 }}>
                  {/* Notifications */}
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Notifications
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.notifications.email}
                          onChange={(e) => handlePreferenceChange('notifications', 'email', e.target.checked)}
                        />
                      }
                      label="Email Notifications"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.notifications.push}
                          onChange={(e) => handlePreferenceChange('notifications', 'push', e.target.checked)}
                        />
                      }
                      label="Push Notifications"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.notifications.sms}
                          onChange={(e) => handlePreferenceChange('notifications', 'sms', e.target.checked)}
                        />
                      }
                      label="SMS Notifications"
                    />
                  </Box>

                  {/* Privacy */}
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Privacy
                    </Typography>
                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel>Profile Visibility</InputLabel>
                      <Select
                        value={preferences.privacy.profileVisibility}
                        label="Profile Visibility"
                        onChange={(e) => handlePreferenceChange('privacy', 'profileVisibility', e.target.value)}
                      >
                        <MenuItem value="public">Public</MenuItem>
                        <MenuItem value="private">Private</MenuItem>
                        <MenuItem value="friends">Friends Only</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.privacy.showEmail}
                          onChange={(e) => handlePreferenceChange('privacy', 'showEmail', e.target.checked)}
                        />
                      }
                      label="Show Email on Profile"
                    />

                  </Box>
                </Box>

                <Box sx={{ mt: 4 }}>
                  <Button 
                    variant="contained" 
                    color="primary"
                    onClick={handlePreferencesSave}
                    disabled={loading}
                  >
                    Save Preferences
                  </Button>
                </Box>
              </Box>
            )}

            {/* Subscriptions Tab */}
            {activeTab === 5 && (
              <SubscriptionManager />
            )}

            {/* Tokens Tab */}
            {activeTab === 4 && (
              <Box>
                {/* Header Section */}
                <Box sx={{ mb: 5 }}>
                  <Typography variant="h3" gutterBottom sx={{ 
                    fontWeight: 700, 
                    color: '#1a1a1a',
                    fontSize: { xs: '1.75rem', sm: '2.125rem' },
                    lineHeight: 1.2
                  }}>
                    Token Management
                  </Typography>
                  <Typography variant="h6" color="text.secondary" sx={{ 
                    maxWidth: 700,
                    lineHeight: 1.5,
                    fontWeight: 400
                  }}>
                    Manage your AI operation credits. Tokens are consumed when you use our AI features for document search, analysis, and generation.
                  </Typography>
                </Box>

                {/* Token Balance Overview */}
                <Box sx={{ mb: 5 }}>
                  <Typography variant="h5" gutterBottom sx={{ 
                    mb: 3, 
                    fontWeight: 600, 
                    color: '#1a1a1a',
                    fontSize: { xs: '1.25rem', sm: '1.5rem' }
                  }}>
                    Current Balance
                  </Typography>
                  
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, 
                    gap: { xs: 2, sm: 3 },
                    mb: 4
                  }}>
                    {/* Available Tokens */}
                    <Card sx={{ 
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: 'white',
                      position: 'relative',
                      overflow: 'hidden',
                      borderRadius: 3,
                      boxShadow: '0 8px 32px rgba(16, 185, 129, 0.3)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 40px rgba(16, 185, 129, 0.4)'
                      }
                    }}>
                      <Box sx={{ 
                        position: 'absolute', 
                        top: -30, 
                        right: -30, 
                        width: 120, 
                        height: 120, 
                        borderRadius: '50%', 
                        background: 'rgba(255,255,255,0.08)',
                        backdropFilter: 'blur(10px)'
                      }} />
                      <CardContent sx={{ position: 'relative', zIndex: 1, p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ 
                            width: 12, 
                            height: 12, 
                            borderRadius: '50%', 
                            bgcolor: 'rgba(255,255,255,0.9)', 
                            mr: 1.5 
                          }} />
                          <Typography variant="overline" sx={{ 
                            opacity: 0.9, 
                            fontWeight: 600,
                            letterSpacing: '0.5px',
                            fontSize: '0.75rem'
                          }}>
                            AVAILABLE
                          </Typography>
                        </Box>
                        <Typography variant="h2" sx={{ 
                          fontWeight: 800, 
                          mb: 1.5,
                          fontSize: { xs: '2.5rem', sm: '3rem' },
                          lineHeight: 1
                        }}>
                          {profile.tokens?.available || 0}
                        </Typography>
                        <Typography variant="body1" sx={{ 
                          opacity: 0.9,
                          fontWeight: 500,
                          fontSize: '0.875rem'
                        }}>
                          Ready to use
                        </Typography>
                      </CardContent>
                    </Card>

                    {/* Used Tokens */}
                    <Card sx={{ 
                      background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                      color: 'white',
                      position: 'relative',
                      overflow: 'hidden',
                      borderRadius: 3,
                      boxShadow: '0 8px 32px rgba(107, 114, 128, 0.3)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 40px rgba(107, 114, 128, 0.4)'
                      }
                    }}>
                      <Box sx={{ 
                        position: 'absolute', 
                        top: -30, 
                        right: -30, 
                        width: 120, 
                        height: 120, 
                        borderRadius: '50%', 
                        background: 'rgba(255,255,255,0.08)',
                        backdropFilter: 'blur(10px)'
                      }} />
                      <CardContent sx={{ position: 'relative', zIndex: 1, p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ 
                            width: 12, 
                            height: 12, 
                            borderRadius: '50%', 
                            bgcolor: 'rgba(255,255,255,0.9)', 
                            mr: 1.5 
                          }} />
                          <Typography variant="overline" sx={{ 
                            opacity: 0.9, 
                            fontWeight: 600,
                            letterSpacing: '0.5px',
                            fontSize: '0.75rem'
                          }}>
                            CONSUMED
                          </Typography>
                        </Box>
                        <Typography variant="h2" sx={{ 
                          fontWeight: 800, 
                          mb: 1.5,
                          fontSize: { xs: '2.5rem', sm: '3rem' },
                          lineHeight: 1
                        }}>
                          {profile.tokens?.used || 0}
                        </Typography>
                        <Typography variant="body1" sx={{ 
                          opacity: 0.9,
                          fontWeight: 500,
                          fontSize: '0.875rem'
                        }}>
                          Total used
                        </Typography>
                      </CardContent>
                    </Card>

                    {/* Purchased Tokens */}
                    <Card sx={{ 
                      background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                      color: 'white',
                      position: 'relative',
                      overflow: 'hidden',
                      borderRadius: 3,
                      boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 40px rgba(59, 130, 246, 0.4)'
                      }
                    }}>
                      <Box sx={{ 
                        position: 'absolute', 
                        top: -30, 
                        right: -30, 
                        width: 120, 
                        height: 120, 
                        borderRadius: '50%', 
                        background: 'rgba(255,255,255,0.08)',
                        backdropFilter: 'blur(10px)'
                      }} />
                      <CardContent sx={{ position: 'relative', zIndex: 1, p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ 
                            width: 12, 
                            height: 12, 
                            borderRadius: '50%', 
                            bgcolor: 'rgba(255,255,255,0.9)', 
                            mr: 1.5 
                          }} />
                          <Typography variant="overline" sx={{ 
                            opacity: 0.9, 
                            fontWeight: 600,
                            letterSpacing: '0.5px',
                            fontSize: '0.75rem'
                          }}>
                            PURCHASED
                          </Typography>
                        </Box>
                        <Typography variant="h2" sx={{ 
                          fontWeight: 800, 
                          mb: 1.5,
                          fontSize: { xs: '2.5rem', sm: '3rem' },
                          lineHeight: 1
                        }}>
                          {profile.tokens?.purchased || 0}
                        </Typography>
                        <Typography variant="body1" sx={{ 
                          opacity: 0.9,
                          fontWeight: 500,
                          fontSize: '0.875rem'
                        }}>
                          Total bought
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
                </Box>

                {/* Token Purchase Section */}
                <Card sx={{ 
                  mb: 5, 
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  border: '1px solid #e2e8f0',
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
                }}>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                      <Box sx={{ 
                        width: 64, 
                        height: 64, 
                        borderRadius: '50%', 
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '2rem',
                        boxShadow: '0 8px 24px rgba(59, 130, 246, 0.4)',
                        flexShrink: 0
                      }}>
                        💳
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h5" gutterBottom sx={{ 
                          fontWeight: 600, 
                          color: '#1a1a1a',
                          mb: 2,
                          fontSize: { xs: '1.25rem', sm: '1.5rem' }
                        }}>
                          Purchase Additional Tokens
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ 
                          mb: 4, 
                          lineHeight: 1.6,
                          fontSize: '1rem'
                        }}>
                          Need more tokens? Purchase additional credits to continue using our AI features. 
                          Tokens are instantly credited to your account.
                        </Typography>
                        
                        <Box sx={{ 
                          display: 'grid', 
                          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, 
                          gap: 3, 
                          mb: 4 
                        }}>
                          {/* Token Package Options */}
                          <Box sx={{ 
                            border: '2px solid #e2e8f0', 
                            borderRadius: 3, 
                            p: 3, 
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            background: '#ffffff',
                            '&:hover': {
                              borderColor: '#3b82f6',
                              transform: 'translateY(-4px)',
                              boxShadow: '0 12px 32px rgba(59, 130, 246, 0.15)',
                              background: '#f8fafc'
                            }
                          }}>
                            <Typography variant="h6" sx={{ 
                              fontWeight: 700, 
                              color: '#3b82f6',
                              mb: 1,
                              fontSize: '1.125rem'
                            }}>
                              1,000 Tokens
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                              Basic Package
                            </Typography>
                          </Box>
                          
                          <Box sx={{ 
                            border: '2px solid #e2e8f0', 
                            borderRadius: 3, 
                            p: 3, 
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            background: '#ffffff',
                            '&:hover': {
                              borderColor: '#3b82f6',
                              transform: 'translateY(-4px)',
                              boxShadow: '0 12px 32px rgba(59, 130, 246, 0.15)',
                              background: '#f8fafc'
                            }
                          }}>
                            <Typography variant="h6" sx={{ 
                              fontWeight: 700, 
                              color: '#3b82f6',
                              mb: 1,
                              fontSize: '1.125rem'
                            }}>
                              5,000 Tokens
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                              Popular Package
                            </Typography>
                          </Box>
                          
                          <Box sx={{ 
                            border: '2px solid #e2e8f0', 
                            borderRadius: 3, 
                            p: 3, 
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            background: '#ffffff',
                            '&:hover': {
                              borderColor: '#3b82f6',
                              transform: 'translateY(-4px)',
                              boxShadow: '0 12px 32px rgba(59, 130, 246, 0.15)',
                              background: '#f8fafc'
                            }
                          }}>
                            <Typography variant="h6" sx={{ 
                              fontWeight: 700, 
                              color: '#3b82f6',
                              mb: 1,
                              fontSize: '1.125rem'
                            }}>
                              10,000 Tokens
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                              Enterprise Package
                            </Typography>
                          </Box>
                        </Box>

                        <Button 
                          variant="contained" 
                          size="large"
                          sx={{ 
                            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                            borderRadius: 3,
                            px: 5,
                            py: 2,
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '1.125rem',
                            boxShadow: '0 8px 24px rgba(59, 130, 246, 0.4)',
                            '&:hover': {
                              boxShadow: '0 12px 32px rgba(59, 130, 246, 0.5)',
                              transform: 'translateY(-2px)'
                            },
                            transition: 'all 0.3s ease'
                          }}
                          onClick={async () => {
                            try {
                              const response = await fetch('/api/v1/subscriptions/subscribe?plan_id=one_time_1000', {
                                method: 'POST',
                                headers: {
                                  'Authorization': `Bearer ${localStorage.getItem('gamora_token')}`,
                                  'Content-Type': 'application/json',
                                }
                              });
                              
                              if (response.ok) {
                                await loadProfile();
                                showSnackbar('✅ 1,000 tokens purchased successfully!', 'success');
                              } else {
                                const errorData = await response.json();
                                showSnackbar(`❌ ${errorData.detail || 'Failed to purchase tokens'}`, 'error');
                              }
                            } catch (error) {
                              showSnackbar('❌ Error purchasing tokens', 'error');
                            }
                          }}
                        >
                          Purchase 1,000 Tokens
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                {/* Token Usage Analytics */}
                <Card sx={{ 
                  mb: 5,
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
                }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h5" gutterBottom sx={{ 
                      fontWeight: 600, 
                      color: '#1a1a1a', 
                      mb: 4,
                      fontSize: { xs: '1.25rem', sm: '1.5rem' }
                    }}>
                      Usage Analytics
                    </Typography>
                    
                    <Box sx={{ 
                      display: 'grid', 
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, 
                      gap: 4 
                    }}>
                      {/* Usage Efficiency */}
                      <Box sx={{ 
                        p: 4, 
                        borderRadius: 3, 
                        background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', 
                        border: '1px solid #bbf7d0',
                        position: 'relative',
                        overflow: 'hidden'
                      }}>
                        <Box sx={{ 
                          position: 'absolute', 
                          top: -20, 
                          right: -20, 
                          width: 80, 
                          height: 80, 
                          borderRadius: '50%', 
                          background: 'rgba(34, 197, 94, 0.1)' 
                        }} />
                        <Box sx={{ position: 'relative', zIndex: 1 }}>
                          <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                            Usage Efficiency
                          </Typography>
                          <Typography variant="h3" sx={{ 
                            fontWeight: 800, 
                            color: '#16a34a',
                            mb: 1,
                            fontSize: { xs: '2rem', sm: '2.5rem' }
                          }}>
                            {profile.tokens?.available > 0 ? 
                              Math.round(((profile.tokens?.available || 0) / ((profile.tokens?.available || 0) + (profile.tokens?.used || 0))) * 100) : 0
                            }%
                          </Typography>
                          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Tokens remaining
                          </Typography>
                        </Box>
                      </Box>

                      {/* Last Purchase */}
                      <Box sx={{ 
                        p: 4, 
                        borderRadius: 3, 
                        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', 
                        border: '1px solid #cbd5e1',
                        position: 'relative',
                        overflow: 'hidden'
                      }}>
                        <Box sx={{ 
                          position: 'absolute', 
                          top: -20, 
                          right: -20, 
                          width: 80, 
                          height: 80, 
                          borderRadius: '50%', 
                          background: 'rgba(100, 116, 139, 0.1)' 
                        }} />
                        <Box sx={{ position: 'relative', zIndex: 1 }}>
                          <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                            Last Purchase
                          </Typography>
                          <Typography variant="h3" sx={{ 
                            fontWeight: 800, 
                            color: '#475569',
                            mb: 1,
                            fontSize: { xs: '2rem', sm: '2.5rem' }
                          }}>
                            {profile.tokens?.lastPurchase ? 
                              new Date(profile.tokens.lastPurchase).toLocaleDateString() : 
                              'Never'
                            }
                          </Typography>
                          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                            {profile.tokens?.lastPurchase ? 
                              `${Math.ceil((Date.now() - new Date(profile.tokens.lastPurchase).getTime()) / (1000 * 60 * 60 * 24))} days ago` : 
                              'No purchases yet'
                            }
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                {/* Token Usage History */}
                {profile.tokens?.lastPurchase && (
                  <Card sx={{ 
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
                  }}>
                    <CardContent sx={{ p: 4 }}>
                      <Typography variant="h5" gutterBottom sx={{ 
                        fontWeight: 600, 
                        color: '#1a1a1a', 
                        mb: 3,
                        fontSize: { xs: '1.25rem', sm: '1.5rem' }
                      }}>
                        Purchase History
                      </Typography>
                      <Box sx={{ 
                        p: 4, 
                        borderRadius: 3, 
                        background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', 
                        border: '1px solid #bbf7d0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 3
                      }}>
                        <Box sx={{ 
                          width: 56, 
                          height: 56, 
                          borderRadius: '50%', 
                          background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '1.5rem',
                          fontWeight: 'bold',
                          boxShadow: '0 4px 16px rgba(22, 163, 74, 0.3)'
                        }}>
                          ✓
                        </Box>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                            Last Purchase Successful
                          </Typography>
                          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                            {new Date(profile.tokens.lastPurchase).toLocaleDateString()} at {new Date(profile.tokens.lastPurchase).toLocaleTimeString()}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Organization Details Dialog */}
      <Dialog open={showOrgDetailsDialog && !!selectedOrg} onClose={handleOrgDetailsDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              {selectedOrg?.name || 'Organization'} Details
            </Typography>
            <IconButton onClick={handleOrgDetailsDialogClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedOrg ? (
            <Box>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={orgDetailsTab} onChange={(e, newValue) => setOrgDetailsTab(newValue)}>
                  <Tab label="Overview" icon={<BusinessIcon />} />
                  <Tab label="Members" icon={<PeopleIcon />} />
                  <Tab label="Invitations" icon={<MailIcon />} />
                </Tabs>
              </Box>

              <TabPanel value={orgDetailsTab} index={0}>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Overview</Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => selectedOrg && loadOrganizationDetails(selectedOrg.id)}
                      disabled={loadingOrgDetails}
                    >
                      Refresh
                    </Button>
                  </Box>
                  <Box sx={{ display: 'grid', gap: 3 }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>Description</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedOrg.description || 'No description provided'}
                      </Typography>
                    </Box>
                    
                    {selectedOrg.website && (
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>Website</Typography>
                        <Typography variant="body2" color="text.secondary">
                          <a href={selectedOrg.website} target="_blank" rel="noopener noreferrer">
                            {selectedOrg.website}
                          </a>
                        </Typography>
                      </Box>
                    )}
                    
                    {selectedOrg.industry && (
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>Industry</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedOrg.industry}
                        </Typography>
                      </Box>
                    )}
                    
                    {selectedOrg.size && (
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>Company Size</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedOrg.size} employees
                        </Typography>
                      </Box>
                    )}
                    
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>Created</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(selectedOrg.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </TabPanel>

              <TabPanel value={orgDetailsTab} index={1}>
                <Box>
                  {(() => {
                    // Get current user's role in this organization
                    const currentUserRole = typeof selectedOrg?.user_role === 'string' ? parseInt(selectedOrg.user_role) : null;
                    const canView = currentUserRole !== null && canViewMembers(currentUserRole);
                    
                    return (
                      <>
                        {/* Permission Notice */}
                        {!canView && (
                          <Alert severity="error" sx={{ mb: 3 }}>
                            <strong>Access Denied:</strong> You don't have permission to view organization members.
                          </Alert>
                        )}
                        
                        {canView && (
                          <>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="h6">
                                Members ({orgMembers.length})
                              </Typography>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => selectedOrg && loadOrganizationDetails(selectedOrg.id)}
                                disabled={loadingOrgDetails}
                              >
                                Refresh
                              </Button>
                            </Box>
                            {loadingOrgDetails ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <Typography>Loading members...</Typography>
                              </Box>
                            ) : orgMembers.length === 0 ? (
                              <Alert severity="info">No members found.</Alert>
                            ) : (
                              <Box>
                                <List>
                                  {orgMembers.map((member, index) => {
                                    try {
                                      // Handle different possible member data structures
                                      const memberAny = member as any;
                                      const memberId = member.id || memberAny.user_id || `member-${index}`;
                                      const memberName = member.user?.username || 
                                                       memberAny.username || 
                                                       memberAny.name || 
                                                       memberAny.user_id || 
                                                       `Member ${index + 1}`;
                                      const memberEmail = member.user?.email || 
                                                        memberAny.email || 
                                                        'No email available';
                                      const memberRole = member.role || OrganizationRole.MEMBER;
                                      
                                      return (
                                        <ListItem key={memberId} divider>
                                          <ListItemIcon>
                                            <Avatar sx={{ width: 32, height: 32 }}>
                                              <PersonIcon />
                                            </Avatar>
                                          </ListItemIcon>
                                          <ListItemText
                                            primary={memberName}
                                            secondary={memberEmail}
                                          />
                                          <Chip
                                            label={getRoleLabel(memberRole)}
                                            color={getRoleColor(memberRole)}
                                            size="small"
                                          />
                                        </ListItem>
                                      );
                                    } catch (error) {
                                      console.error('Error rendering member:', member, error);
                                      return (
                                        <ListItem key={`error-${index}`} divider>
                                          <ListItemText
                                            primary="Error rendering member"
                                            secondary={`Member ID: ${member.id || 'Unknown'}`}
                                          />
                                        </ListItem>
                                      );
                                    }
                                  })}
                                </List>
                              </Box>
                            )}
                          </>
                        )}
                      </>
                    );
                  })()}
                </Box>
              </TabPanel>

              <TabPanel value={orgDetailsTab} index={2}>
                <Box>
                  {(() => {
                    // Get current user's role in this organization
                    const currentUserRole = typeof selectedOrg?.user_role === 'string' ? parseInt(selectedOrg.user_role) : null;
                    const canView = currentUserRole !== null && canViewInvitations(currentUserRole);
                    
                    return (
                      <>
                        {/* Permission Notice */}
                        {!canView && (
                          <Alert severity="error" sx={{ mb: 3 }}>
                            <strong>Access Denied:</strong> You don't have permission to view organization invitations.
                          </Alert>
                        )}
                        
                        {canView && (
                          <>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="h6">
                                Invitations ({orgInvitations.length})
                              </Typography>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => selectedOrg && loadOrganizationDetails(selectedOrg.id)}
                                disabled={loadingOrgDetails}
                              >
                                Refresh
                              </Button>
                            </Box>
                            {loadingOrgDetails ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <Typography>Loading invitations...</Typography>
                              </Box>
                            ) : orgInvitations.length === 0 ? (
                              <Alert severity="info">No pending invitations.</Alert>
                            ) : (
                              <List>
                                {orgInvitations.map((invitation) => (
                                  <ListItem key={invitation.id} divider>
                                    <ListItemIcon>
                                      <MailIcon />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={invitation.email}
                                      secondary={`Role: ${getRoleLabel(invitation.role)} • Sent: ${new Date(invitation.created_at).toLocaleDateString()}`}
                                    />
                                    <Chip
                                      label={getInvitationStatusLabel(invitation.status)}
                                      color={(typeof invitation.status === "string" ? parseInt(invitation.status) : invitation.status) === InvitationStatus.PENDING ? 'warning' : (typeof invitation.status === "string" ? parseInt(invitation.status) : invitation.status) === InvitationStatus.ACCEPTED ? 'success' : 'error'}
                                      size="small"
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            )}
                          </>
                        )}
                      </>
                    );
                  })()}
                </Box>
              </TabPanel>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <Typography>No organization selected.</Typography>
            </Box>
          )}
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={handleOrgDetailsDialogClose}>
            Close
          </Button>
        </Box>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={showAddMemberDialog} onClose={handleAddMemberCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Add Member to {selectedOrg?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 3, pt: 1 }}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={addMemberForm.email}
              onChange={(e) => setAddMemberForm({ ...addMemberForm, email: e.target.value })}
              required
            />
            <FormControl fullWidth>
              <InputLabel>Final Role</InputLabel>
              <Select
                value={addMemberForm.role}
                label="Final Role"
                onChange={(e) => setAddMemberForm({ ...addMemberForm, role: e.target.value as OrganizationRole })}
              >
                <MenuItem value={OrganizationRole.MEMBER}>Member</MenuItem>
                <MenuItem value={OrganizationRole.ADMIN}>Admin</MenuItem>
                <MenuItem value={OrganizationRole.VIEWER}>Viewer</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Personal Message (Optional)"
              value={addMemberForm.message}
              onChange={(e) => setAddMemberForm({ ...addMemberForm, message: e.target.value })}
              multiline
              rows={3}
              placeholder="Add a personal message to your invitation..."
            />
          </Box>
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={handleAddMemberCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddMemberSubmit}
            disabled={loading || !addMemberForm.email.trim()}
          >
            Send Invitation
          </Button>
        </Box>
      </Dialog>

      {/* Create Organization Dialog */}
      <Dialog open={showCreateOrgDialog} onClose={handleCreateOrgCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Organization</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 3, pt: 1 }}>
            <TextField
              fullWidth
              label="Organization Name"
              value={createOrgForm.name}
              onChange={(e) => setCreateOrgForm({ ...createOrgForm, name: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Description"
              value={createOrgForm.description}
              onChange={(e) => setCreateOrgForm({ ...createOrgForm, description: e.target.value })}
              multiline
              rows={3}
            />

            <TextField
              fullWidth
              label="Website"
              value={createOrgForm.website}
              onChange={(e) => setCreateOrgForm({ ...createOrgForm, website: e.target.value })}
              placeholder="https://example.com"
            />
            <FormControl fullWidth>
              <InputLabel>Industry</InputLabel>
              <Select
                value={createOrgForm.industry}
                label="Industry"
                onChange={(e) => setCreateOrgForm({ ...createOrgForm, industry: e.target.value })}
              >
                <MenuItem value="">Select industry</MenuItem>
                <MenuItem value="Medical Device Manufacturing">Medical Device Manufacturing</MenuItem>
                <MenuItem value="Regulatory Affairs">Regulatory Affairs</MenuItem>
                <MenuItem value="Quality Assurance">Quality Assurance</MenuItem>
                <MenuItem value="Clinical Research">Clinical Research</MenuItem>
                <MenuItem value="Healthcare Software">Healthcare Software</MenuItem>
                <MenuItem value="Consulting">Consulting</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Company Size</InputLabel>
              <Select
                value={createOrgForm.size}
                label="Company Size"
                onChange={(e) => setCreateOrgForm({ ...createOrgForm, size: e.target.value })}
              >
                <MenuItem value="">Select size</MenuItem>
                <MenuItem value="1-10">1-10 employees</MenuItem>
                <MenuItem value="11-50">11-50 employees</MenuItem>
                <MenuItem value="51-200">51-200 employees</MenuItem>
                <MenuItem value="201-500">201-500 employees</MenuItem>
                <MenuItem value="500+">500+ employees</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={handleCreateOrgCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateOrgSubmit}
            disabled={loading || !createOrgForm.name.trim()}
          >
            Create Organization
          </Button>
        </Box>
      </Dialog>
    </Dialog>
  );
};

export default ProfileModal;
